// @flow

import * as React from 'react';

import { useTranslator } from '@gandi/translate';

import { withRouter } from 'src/helpers/withRouter';
import { useAppSelector } from 'src/hooks/useAppSelector';

import { Intro } from '../Layout/components/Intro/Intro';
import { Layout } from '../components/Layout';
import { handleMarketingParams } from '../modules/app/actions';
import type { StaticHandlerArgsWithMarketing } from '../modules/app/actions';
import type { PreFetchArgs, PreFetchReturn, WithRouterProps } from '../types';

import { CertificateForm } from './components/CertificateForm';
import { StepCSR } from './components/StepCSR';
import { StepType } from './components/StepType';
import { StepValidation } from './components/StepValidation';
import { Step, Wizard } from './components/Wizard';
import {
  CERT_ALTNAMES_INPUT_NAME,
  CERT_CN_INPUT_NAME,
  CERT_DCV_METHOD_INPUT_NAME,
  CERT_NUMBER_ADDRESSES_INPUT_NAME,
  CERT_TYPE_PROTECT_INPUT_NAME,
  STEP_CSR_FIELDS,
  STEP_TYPE_FIELDS,
} from './consts';
import { mapFormValuesCreateToProductData } from './form/mapper';
import {
  preFetchCertificatesPrices,
  useCertificatePrices,
} from './hooks/prices';
import { selectCertificateCreatePossibilities } from './modules/certificate';
import type { FormValues } from './types';

type Props = WithRouterProps<{
  cn: string,
}>;

function CertificateConcrete({ params }: Props): React.Node {
  const { __ } = useTranslator();
  const { cn } = params;

  const certificateCreatePossibilities = useAppSelector(state =>
    selectCertificateCreatePossibilities(state),
  );

  // Load prices used in the wizard
  useCertificatePrices({ process: 'create' });

  const formDataMapper = (formValues: FormValues) =>
    mapFormValuesCreateToProductData(
      formValues,
      certificateCreatePossibilities,
    );

  const initialValues: Partial<FormValues> = {
    [CERT_CN_INPUT_NAME]: cn,
    [CERT_TYPE_PROTECT_INPUT_NAME]: 'multi', // force type_protect which is set to single by default
    [CERT_NUMBER_ADDRESSES_INPUT_NAME]: '3',
    [CERT_ALTNAMES_INPUT_NAME]: [''],
    [CERT_DCV_METHOD_INPUT_NAME]: 'auto',
  };

  const title = __('Secure your Web Hosting: %(cn)s', {
    cn,
  });

  return (
    <Layout>
      <Intro title={title} />
      <CertificateForm
        formDataMapper={formDataMapper}
        initialValues={initialValues}
      >
        <Wizard>
          <Step stepFields={STEP_TYPE_FIELDS}>
            <StepType process="create" isSHS />
          </Step>
          <Step stepFields={STEP_CSR_FIELDS}>
            <StepCSR isStored formDataMapper={formDataMapper} />
          </Step>
          <Step>
            <StepValidation forceAutoDCV />
          </Step>
        </Wizard>
      </CertificateForm>
    </Layout>
  );
}

CertificateConcrete.handleRedirect = async (
  args: StaticHandlerArgsWithMarketing<>,
) => {
  await handleMarketingParams(args);
  await Layout.handleRedirect(args);
};

CertificateConcrete.preFetch = async (
  args: PreFetchArgs<>,
): PreFetchReturn<> => {
  // XXX: there is an issue (on the whole shop?) there is no grid fetch on client side.
  // only SSR (`Layout.preFetch` does it somewhere). So if the prefetch has been
  // missed, the prices will be based on the A grid only.
  await Layout.preFetch(args);

  return preFetchCertificatesPrices({ ...args, process: 'create' });
};

export const CreateSHSCertificate: React.ComponentType<{}> =
  withRouter(CertificateConcrete);
